import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import moment from 'moment'
import message from 'antd/lib/message'
import { useRouteMatch } from 'react-router-dom'

import { analytics, apiManager, form, strings } from 'helpers'
import { useLogin } from 'hooks'
import { SIGNUP_STEPS as STEPS } from 'constants/auth'
import { PROFILE_CONTAINER, PROFILE_CONTAINER_STYLES } from '../styles'

import DefaultInput from '../components/Input'
import CloseButton from '../components/CloseButton'
import Description from '../components/Description'
import Label from '../components/Label'
import Button from '../components/Button'
import MotionContainer from '../components/MotionContainer'
import ProgressBar from '../components/ProgressBar'

const STEPS_COLLECTIONS = Object.values(STEPS)

const Input = props => (
  <DefaultInput
    classNames={{ input: 'border-white bg-white bg-opacity-10' }}
    useBaseInputStyles
    {...props}
  />
)

const PREVIOUS_STEPS_COUNT = 1

const SignupFormStep = ({ onClose, onSubmit, autoFocus, user, signupStep }) => {
  const match = useRouteMatch()
  const referralCode = match?.params?.referralCode
  const login = useLogin()
  const submitButtonRef = useRef()

  const [currentStep, setCurrentStep] = useState(STEPS.NAME)
  const [userData, setUserData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    instagramLink: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { currentStepIndex, isLastStep, isFirstStep, progress } = useMemo(() => {
    const currentStepIndex = STEPS_COLLECTIONS.findIndex(step => step === currentStep)

    return {
      currentStepIndex,
      isLastStep: currentStepIndex === STEPS_COLLECTIONS.length - 1,
      isFirstStep: currentStepIndex === 0 || signupStep === currentStep,
      progress:
        ((currentStepIndex + 1 + PREVIOUS_STEPS_COUNT) /
          (STEPS_COLLECTIONS.length + PREVIOUS_STEPS_COUNT)) *
        100,
    }
  }, [currentStep, signupStep])

  const submitButton = useMemo(() => {
    let isDisabled

    switch (currentStep) {
      case STEPS.NAME: {
        isDisabled = !userData.firstName || !userData.lastName
        break
      }
      default: {
        isDisabled = !userData[currentStep]
      }
    }

    return { isDisabled }
  }, [currentStep, userData])

  const goToStep = useCallback(
    direction => {
      const nextStepIndex = currentStepIndex + direction
      setCurrentStep(STEPS_COLLECTIONS[nextStepIndex])
    },
    [currentStepIndex]
  )

  const closeButton = useMemo(() => {
    return {
      isVisible: true,
      onClose: isFirstStep ? onClose : () => goToStep(-1),
    }
  }, [onClose, goToStep, isFirstStep])

  useEffect(() => {
    analytics.segmentTrack(
      `Anonymous Is In Sign Up ${strings.capitalizeFirstLetter(currentStep)} Step`
    )
  }, [currentStep])

  const signup = useCallback(async () => {
    const { phoneNumber } = user
    const { firstName, lastName, email, instagramLink } = userData

    const signupData = {
      email,
      phoneNumber,
      name: firstName,
      firstName,
      lastName,
      referralCode,
      instagramLink: `https://www.instagram.com/${instagramLink.replace('@', '')}`,
      authMethod: 'Phone',
      timestamp: moment().unix(),
      signupSource: 'webapp',
    }

    try {
      setIsSubmitting(true)

      const data = await apiManager.signupUser(signupData)
      const userId = data.userId
      const jwt = data.jwt
      const chatToken = data.chatToken

      const loginData = await login({ chatToken, jwt, userId, newUser: true })
      setUserData({ ...userData, ...loginData })
      analytics.segmentTrack('Signup Completed')

      onSubmit && onSubmit(true)
    } catch (error) {
      message.error(error.message || 'Please try again')
    } finally {
      setIsSubmitting(false)
    }
  }, [user, userData, referralCode, login, onSubmit])

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()

      if (!form.checkValidity(e)) {
        return
      }

      if (isLastStep) {
        await signup()
        return
      }

      goToStep(1)
    },
    [goToStep, signup, isLastStep]
  )

  const handleChange = useCallback(
    e =>
      setUserData({
        ...userData,
        [e.target.name]: e.target.value,
      }),
    [userData]
  )

  const renderSteps = useMemo(() => {
    switch (currentStep) {
      case STEPS.NAME: {
        return (
          <MotionContainer key={currentStep}>
            <Label>Name</Label>
            <Input
              key={autoFocus}
              autoFocus={autoFocus}
              value={userData.firstName}
              type="text"
              placeholder="First Name"
              required
              name="firstName"
              onChange={handleChange}
            />
            <br />
            <Input
              value={userData.lastName}
              type="text"
              placeholder="Last Name (private)"
              required
              name="lastName"
              onChange={handleChange}
            />
            <Description>Your First Name will be public.</Description>
          </MotionContainer>
        )
      }
      case STEPS.EMAIL: {
        return (
          <MotionContainer>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="Email"
              name="email"
              value={userData.email}
              required
              onChange={handleChange}
              autoFocus={autoFocus}
            />
            <Description>Secure your account by connecting an email.</Description>
          </MotionContainer>
        )
      }
      case STEPS.INSTAGRAM: {
        return (
          <MotionContainer>
            <Label>Instagram</Label>
            <Input
              type="text"
              placeholder="@username"
              name="instagramLink"
              value={userData.instagramLink}
              required
              onChange={handleChange}
              autoFocus={autoFocus}
            />
          </MotionContainer>
        )
      }
      default:
        return null
    }
  }, [currentStep, handleChange, userData, autoFocus])

  return (
    <>
      <form
        noValidate
        onSubmit={handleSubmit}
        className={classnames(PROFILE_CONTAINER, 'justify-between')}
        style={PROFILE_CONTAINER_STYLES}
      >
        {closeButton.isVisible && (
          <CloseButton onClose={closeButton.onClose} Icon={closeButton.Icon} />
        )}

        <div className="w-full">
          <ProgressBar progress={progress} />
          {renderSteps}
        </div>
        <div className="flex flex-col w-full items-center">
          <Button
            type="submit"
            className="order-1 w-full mt-6 mx-auto text-base sm:order-none"
            disabled={submitButton.isDisabled}
            loading={isSubmitting}
            ref={submitButtonRef}
          >
            Continue
          </Button>
        </div>
      </form>
    </>
  )
}

SignupFormStep.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  autoFocus: PropTypes.bool,
  user: PropTypes.object,
  event: PropTypes.object,
}

SignupFormStep.defaultProps = {
  autoFocus: false,
}

export default SignupFormStep
